import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Field,
  IconButton,
  SingleSelectDropdown,
  ZoomPanWrapper,
} from "@carbon6/c6-frontend-design";
import {
  getAnnotationBundleFrame,
  getAnnotationSet,
  listAnnotationBundleFrames,
  requestAnnotationBundleIndex,
  type AnnotationBundleFrameDetail,
  type AnnotationBundleFrameSummary,
  type AnnotationBundleIndexView,
  type AnnotationSetSummaryView,
} from "@/api/trainingAnnotation";
import { fetchSignedText } from "@/http";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./AnnotationBundlePanel.module.css";

const PAGE_SIZE = 20;
const POLL_MS = 3000;

export function AnnotationBundlePanel({ set }: { set: AnnotationSetSummaryView }) {
  const [index, setIndex] = useState<AnnotationBundleIndexView | null>(set.bundle_index);
  const [camera, setCamera] = useState("");
  const [page, setPage] = useState(1);
  // null 은 아직 받지 못한 목록이다. 빈 배열은 조회가 성공했고 프레임이 없는 것이다.
  const [frames, setFrames] = useState<readonly AnnotationBundleFrameSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AnnotationBundleFrameDetail | null>(null);
  // 조회 실패는 실패한 자리가 알린다. 그래서 조회마다 따로 둔다. 한 줄을 같이 쓰면 다른 자리의
  // 대기 문구와 실패 문구가 함께 남는다.
  const [statusFailed, setStatusFailed] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // 프레임 하나만 다시 받는다. `refresh` 는 목록부터 다시 받아서 고른 프레임을 잃는다.
  const [detailRetry, setDetailRetry] = useState(0);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    setStatusFailed(false);
    async function poll() {
      try {
        const next = (await getAnnotationSet(set.uuid, { signal: controller.signal })).bundle_index;
        if (controller.signal.aborted) return;
        setIndex(next);
        if (next?.status === "queued" || next?.status === "running")
          timer = setTimeout(() => void poll(), POLL_MS);
      } catch {
        if (!controller.signal.aborted) setStatusFailed(true);
      }
    }
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [set.uuid, refresh]);

  useEffect(() => {
    if (index?.status !== "ready") return;
    const controller = new AbortController();
    setFrames(null);
    setSelected(null);
    setDetail(null);
    setListFailed(false);
    setDetailFailed(false);
    void listAnnotationBundleFrames(
      set.uuid,
      { page, size: PAGE_SIZE, camera: camera || undefined },
      { signal: controller.signal },
    )
      .then((result) => {
        if (controller.signal.aborted) return;
        setFrames(result.items);
        setTotal(result.total);
        setSelected(result.items[0]?.uuid ?? null);
      })
      .catch(() => {
        if (!controller.signal.aborted) setListFailed(true);
      });
    return () => controller.abort();
  }, [set.uuid, index?.status, camera, page, refresh]);

  useEffect(() => {
    if (selected === null) return;
    const controller = new AbortController();
    setDetail(null);
    setDetailFailed(false);
    void getAnnotationBundleFrame(set.uuid, selected, { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) setDetail(result);
      })
      .catch(() => {
        if (!controller.signal.aborted) setDetailFailed(true);
      });
    return () => controller.abort();
  }, [set.uuid, selected, refresh, detailRetry]);

  async function requestIndex() {
    if (requesting) return;
    setRequesting(true);
    setRequestFailed(false);
    try {
      setIndex(await requestAnnotationBundleIndex(set.uuid));
      setRefresh((value) => value + 1);
    } catch {
      setRequestFailed(true);
    } finally {
      setRequesting(false);
    }
  }

  const reload = () => setRefresh((value) => value + 1);
  const listLoading = frames === null && !listFailed;

  return (
    <section className={styles.panel} aria-label="Annotation 번들 뷰어">
      <div className={styles.toolbar}>
        <h3>{set.origin === "upload_bundle" ? "업로드한 기본 번들" : "파일에서 가져온 번들"}</h3>
        <IconButton text="새로고침" size="sm" onClick={reload} />
      </div>
      <p>PNG와 YAML의 점을 읽기 전용으로 표시합니다.</p>
      {statusFailed ? (
        <EmptyState tone="error" action={{ label: "다시 시도", onClick: reload }}>
          변환 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </EmptyState>
      ) : index === null ? (
        <LoadingState>변환 상태를 확인하고 있습니다.</LoadingState>
      ) : index.status === "queued" || index.status === "running" ? (
        <LoadingState>
          {index.status === "queued" ? "변환 대기 중" : "변환 중"} · {index.processed_count} /{" "}
          {index.requested_count} 프레임
        </LoadingState>
      ) : index.status === "ready" ? (
        <>
          <div className={styles.toolbar}>
            <SingleSelectDropdown
              label="카메라"
              value={camera}
              options={[
                { value: "", label: "전체 카메라" },
                ...(set.bundle?.cameras ?? []).map((item) => ({
                  value: item.camera,
                  label: item.camera,
                })),
              ]}
              onValueChange={(value) => {
                setCamera(value);
                setPage(1);
              }}
            />
            <span>{total} 프레임</span>
            <IconButton
              text="이전"
              size="sm"
              disabled={page === 1 || listLoading}
              onClick={() => setPage((value) => value - 1)}
            />
            <span>
              {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <IconButton
              text="다음"
              size="sm"
              disabled={page * PAGE_SIZE >= total || listLoading}
              onClick={() => setPage((value) => value + 1)}
            />
          </div>
          <div className={styles.workspace}>
            <nav className={styles.files} aria-label="번들 프레임">
              {listFailed ? (
                <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: reload }}>
                  프레임 목록을 불러오지 못했습니다.
                </EmptyState>
              ) : frames === null ? (
                <LoadingState size="sm">프레임 목록을 불러오는 중…</LoadingState>
              ) : frames.length === 0 ? (
                <EmptyState size="sm">프레임이 없습니다.</EmptyState>
              ) : (
                frames.map((frame) => (
                  <IconButton
                    key={frame.uuid}
                    text={frame.png.relative_path}
                    title={`${frame.width} × ${frame.height} · 객체 ${frame.object_count}개 · 점 ${frame.point_count}개`}
                    aria-pressed={frame.uuid === selected}
                    variant="surface"
                    onClick={() => setSelected(frame.uuid)}
                  />
                ))
              )}
            </nav>
            <div className={styles.detail}>
              {detailFailed ? (
                <EmptyState
                  tone="error"
                  action={{
                    label: "다시 시도",
                    onClick: () => setDetailRetry((value) => value + 1),
                  }}
                >
                  프레임을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                </EmptyState>
              ) : detail ? (
                <BundleFrame key={`${detail.uuid}:${detail.png.url}`} frame={detail} />
              ) : selected !== null ? (
                <LoadingState>프레임을 불러오는 중…</LoadingState>
              ) : null}
            </div>
          </div>
        </>
      ) : (
        // 미변환과 변환 실패. 같은 버튼으로 변환을 요청한다. 요청이 실패하면 이 자리가 알린다.
        <EmptyState
          tone={requestFailed || index.status === "failed" ? "error" : "neutral"}
          action={{
            label: index.status === "failed" ? "변환 재시도" : "시각화 준비",
            onClick: () => void requestIndex(),
          }}
        >
          {requestFailed ? "변환을 요청하지 못했습니다. 다시 시도해 주세요." : indexNotice(index)}
        </EmptyState>
      )}
    </section>
  );
}

// 미변환·변환 실패 자리의 문장. 서버가 준 실패 사유가 있으면 그 사유를 표시한다.
function indexNotice(index: AnnotationBundleIndexView): string {
  if (index.error !== null) {
    const path = index.error.relative_path ? ` (${index.error.relative_path})` : "";
    return `${index.error.message}${path}`;
  }
  return index.status === "failed"
    ? "변환에 실패했습니다. [변환 재시도] 로 다시 요청해 주세요."
    : "아직 시각화용 변환을 하지 않았습니다. [시각화 준비] 로 변환을 시작해 주세요.";
}

function BundleFrame({ frame }: { frame: AnnotationBundleFrameDetail }) {
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const [showYaml, setShowYaml] = useState(false);
  const [yaml, setYaml] = useState<string | null>(null);
  const [yamlError, setYamlError] = useState(false);
  const [imageStatus, setImageStatus] = useState<"loading" | "ready" | "failed">("loading");
  useEffect(() => {
    if (!showYaml) return;
    const controller = new AbortController();
    setYamlError(false);
    void fetchSignedText(frame.yaml.url, controller.signal)
      .then((text) => {
        if (!controller.signal.aborted) setYaml(text);
      })
      .catch(() => {
        if (!controller.signal.aborted) setYamlError(true);
      });
    return () => controller.abort();
  }, [frame.yaml.url, showYaml]);
  useEffect(() => {
    const timer = setTimeout(
      () => setImageStatus((value) => (value === "loading" ? "failed" : value)),
      30000,
    );
    return () => clearTimeout(timer);
  }, [frame.png.url]);
  // DOM 수를 줄이려고 점을 객체·라벨별 SVG 경로로 묶는다.
  const paths = useMemo(() => {
    const radius = Math.max(frame.width, frame.height) / 180;
    return frame.objects.map((object) => {
      const foreground: string[] = [];
      const background: string[] = [];
      object.points.forEach(([x, y], index) => {
        const target = object.labels[index] === 1 ? foreground : background;
        target.push(
          `M ${x} ${y} m ${-radius} 0 a ${radius} ${radius} 0 1 0 ${radius * 2} 0 a ${radius} ${radius} 0 1 0 ${-radius * 2} 0`,
        );
      });
      return {
        ordinal: object.ordinal,
        foreground: foreground.join(" "),
        background: background.join(" "),
      };
    });
  }, [frame]);
  const outside = frame.objects.reduce(
    (count, object) =>
      count +
      object.points.filter(([x, y]) => x < 0 || y < 0 || x >= frame.width || y >= frame.height)
        .length,
    0,
  );
  return (
    <>
      <div className={styles.toolbar}>
        <strong>{frame.png.relative_path}</strong>
        <span>
          {frame.width} × {frame.height}
        </span>
        <a href={frame.png.url} target="_blank" rel="noreferrer">
          PNG 파일 열기
        </a>
        <a href={frame.yaml.url} target="_blank" rel="noreferrer">
          YAML 파일 열기
        </a>
      </div>
      <p>Ctrl + 휠로 확대하고 Ctrl + 드래그로 이동합니다. 채운 점은 전경, 빈 점은 배경입니다.</p>
      <ZoomPanWrapper
        key={frame.uuid}
        className={styles.viewport}
        showZoomControls
        resetLabel="화면에 맞추기"
        zoomInLabel="확대"
        zoomOutLabel="축소"
        maxZoom={16}
        preserveRelativeViewOnResize
      >
        <div className={styles.canvas}>
          <img
            src={frame.png.url}
            alt={frame.png.relative_path}
            className={styles.image}
            // 받기 전에도 이미지와 같은 비율의 자리를 잡는다. 도착해도 아래 내용이 밀리지 않는다.
            style={{ aspectRatio: `${frame.width} / ${frame.height}` }}
            onLoad={() => setImageStatus("ready")}
            onError={() => setImageStatus("failed")}
          />
          {imageStatus !== "ready" && (
            <div className={`${stateFrameClassName} ${styles.imageState}`}>
              {imageStatus === "loading" ? (
                <LoadingState mode="plain">이미지를 불러오는 중…</LoadingState>
              ) : (
                <EmptyState mode="plain" tone="error">
                  이미지를 불러오지 못했습니다. 새로고침해 주세요.
                </EmptyState>
              )}
            </div>
          )}
          <svg
            viewBox={`0 0 ${frame.width} ${frame.height}`}
            className={styles.points}
            role="img"
            aria-label={`${frame.png.relative_path} Annotation`}
          >
            {imageStatus === "ready" &&
              paths
                .filter((object) => !hidden.has(object.ordinal))
                .map((object) => (
                  <g key={object.ordinal} aria-label={`객체 ${object.ordinal + 1}`}>
                    <path
                      d={object.foreground}
                      className={styles.foreground}
                      vectorEffect="non-scaling-stroke"
                    >
                      <title>{`객체 ${object.ordinal + 1} 전경`}</title>
                    </path>
                    <path
                      d={object.background}
                      className={styles.background}
                      vectorEffect="non-scaling-stroke"
                    >
                      <title>{`객체 ${object.ordinal + 1} 배경`}</title>
                    </path>
                  </g>
                ))}
          </svg>
        </div>
      </ZoomPanWrapper>
      {outside > 0 && (
        <p role="status">이미지 범위 밖의 점 {outside}개를 원래 좌표로 보존했습니다.</p>
      )}
      <ul className={styles.objects}>
        {frame.objects.map((object) => (
          <li key={object.ordinal}>
            <Field label={`객체 ${object.ordinal + 1}`} layout="hug" labelPlacement="end">
              <Checkbox
                checked={!hidden.has(object.ordinal)}
                onChange={(event) =>
                  setHidden((previous) => {
                    const next = new Set(previous);
                    if (event.target.checked) next.delete(object.ordinal);
                    else next.add(object.ordinal);
                    return next;
                  })
                }
              />
            </Field>
            {object.source_index !== null && <span>원본 번호 {object.source_index}</span>}
            <span>
              전경 {object.labels.filter((label) => label === 1).length} · 배경{" "}
              {object.labels.filter((label) => label === 0).length}
            </span>
          </li>
        ))}
      </ul>
      <IconButton
        text={showYaml ? "YAML 닫기" : "YAML 원문 보기"}
        size="sm"
        onClick={() => setShowYaml((value) => !value)}
      />
      {showYaml &&
        (yamlError ? (
          <EmptyState size="sm" tone="error">
            YAML을 불러오지 못했습니다. 새로고침해 주세요.
          </EmptyState>
        ) : yaml === null ? (
          <LoadingState size="sm">YAML을 불러오는 중…</LoadingState>
        ) : (
          <pre className={styles.yaml} aria-label="YAML 원문">
            {yaml}
          </pre>
        ))}
    </>
  );
}
